import { type RefObject, useState } from 'react';
import { useIsomorphicLayoutEffect } from '@direct-frontend/stdlib';

/**
 * Следит за началом тела грида в документе.
 * Изменения размеров объединяются в один замер на кадр; прокрутка не требует нового замера.
 */
export function useWindowScrollMargin(bodyRef: RefObject<HTMLDivElement>) {
    const [scrollMargin, setScrollMargin] = useState<number>();

    useIsomorphicLayoutEffect(() => {
        const body = bodyRef.current;
        const ownerWindow = body?.ownerDocument.defaultView;
        if (!body || !ownerWindow) {
            return;
        }

        let animationFrame: number | undefined;

        const update = () => {
            const nextMargin = body.getBoundingClientRect().top + ownerWindow.scrollY;
            setScrollMargin(previous => (previous === nextMargin ? previous : nextMargin));
        };

        const scheduleUpdate = () => {
            if (animationFrame !== undefined) {
                return;
            }

            animationFrame = ownerWindow.requestAnimationFrame(() => {
                animationFrame = undefined;
                update();
            });
        };

        update();

        const resizeObserver = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(scheduleUpdate);

        // На положение грида влияют его предки и блоки перед ним, включая шапку и фильтры.
        // Не наблюдаем поддерево строк: их размеры обрабатывает RowMeasurer.
        let current: Element | null = body;
        while (current) {
            let preceding = current.previousElementSibling;
            while (preceding) {
                resizeObserver?.observe(preceding);
                preceding = preceding.previousElementSibling;
            }

            current = current.parentElement;
            if (current) {
                resizeObserver?.observe(current);
            }
        }

        ownerWindow.addEventListener('resize', scheduleUpdate, { passive: true });

        return () => {
            resizeObserver?.disconnect();
            ownerWindow.removeEventListener('resize', scheduleUpdate);
            if (animationFrame !== undefined) {
                ownerWindow.cancelAnimationFrame(animationFrame);
            }
        };
    }, [bodyRef]);

    return { scrollMargin: scrollMargin ?? 0, isReady: scrollMargin !== undefined };
}
